/**
 * PackageApplyControl — Ted #5 (#908).
 *
 * "In Gingr, when I begin her checkout, I have the option to apply her
 * existing package and deduct one daycare visit rather than charging
 * separately for that visit." Until this control existed the orchestrator
 * redeemed a day unconditionally and the row only PREVIEWED the balance.
 *
 * The desk sees which package(s) the customer holds for this service, an
 * "Apply package" toggle (ON by default — the package is what they paid for),
 * a picker when more than one is active, and either the balance preview
 * ("3 days → 2 days after this") or, when unticked, what the visit will be
 * charged instead. The Bookings orchestrator reads the choice at checkout.
 */
import type { JSX } from 'react';
import { PackageBalance, defaultPackageLabel, type CustomerPackage } from './PackageBalance.js';
import './PackageApplyControl.css';

export interface PackageChoice {
  /** Apply a package day to this visit (default true when a package exists). */
  readonly apply: boolean;
  /** Which package, when the customer holds more than one; null = the first (oldest). */
  readonly packageId: string | null;
}

export const DEFAULT_PACKAGE_CHOICE: PackageChoice = { apply: true, packageId: null };

/** The package a choice resolves to: the picked one, else the first listed. */
export function resolveChosenPackage(
  packages: ReadonlyArray<CustomerPackage>,
  choice: PackageChoice,
): CustomerPackage | null {
  if (packages.length === 0) return null;
  if (choice.packageId) {
    return packages.find((p) => p.id === choice.packageId) ?? packages[0] ?? null;
  }
  return packages[0] ?? null;
}

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function packageLabel(pkg: CustomerPackage): string {
  return pkg.label ?? defaultPackageLabel(pkg);
}

export interface PackageApplyControlProps {
  /** Active packages matching this booking's service kind (at least one). */
  readonly packages: ReadonlyArray<CustomerPackage>;
  readonly choice: PackageChoice;
  /** What the visit costs when the package is NOT applied. */
  readonly chargeCents: number;
  /**
   * #1157 E2.4 — package days this visit draws: one per dog on the booking.
   * Defaults to 1 (a one-dog visit).
   */
  readonly daysNeeded?: number;
  readonly disabled?: boolean;
  /** Scopes test ids by booking id. */
  readonly testIdSuffix: string;
  readonly onChange: (next: PackageChoice) => void;
}

export function PackageApplyControl({
  packages,
  choice,
  chargeCents,
  daysNeeded = 1,
  disabled = false,
  testIdSuffix,
  onChange,
}: PackageApplyControlProps): JSX.Element | null {
  const chosen = resolveChosenPackage(packages, choice);
  if (!chosen) return null;
  const suffix = `-${testIdSuffix}`;
  const selectId = `package-apply-select${suffix}`;

  return (
    <div className="ferro-package-apply" data-testid={`package-apply${suffix}`}>
      <label className="ferro-field ferro-field--inline ferro-package-apply__toggle">
        <input
          type="checkbox"
          checked={choice.apply}
          disabled={disabled}
          onChange={(e) => onChange({ ...choice, apply: e.target.checked })}
          data-testid={`package-apply-toggle${suffix}`}
        />
        <span>
          Apply package — {packageLabel(chosen)} · {chosen.remainingDays} of{' '}
          {chosen.totalDays} day{chosen.totalDays === 1 ? '' : 's'} left
        </span>
      </label>
      {packages.length > 1 ? (
        <label className="ferro-field ferro-package-apply__picker" htmlFor={selectId}>
          <span className="ferro-field__label">Which package</span>
          <select
            id={selectId}
            className="ferro-field__input"
            value={chosen.id}
            disabled={disabled || !choice.apply}
            onChange={(e) => onChange({ ...choice, packageId: e.target.value })}
            data-testid={selectId}
          >
            {packages.map((p) => (
              <option key={p.id} value={p.id}>
                {packageLabel(p)} · {p.remainingDays} of {p.totalDays} left
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {choice.apply && chosen.remainingDays < daysNeeded ? (
        // #1157 E2.4 — the checkout draws the whole visit or none of it (the
        // server refuses a partial draw), so a short package is said here,
        // before the click, not discovered after it.
        <p
          className="ferro-field__hint ferro-package-apply__short"
          role="status"
          data-testid={`package-apply-short${suffix}`}
        >
          This visit needs {daysNeeded} package days (one per dog) and the package has{' '}
          {chosen.remainingDays} left — it will be charged {formatMoney(chargeCents)} and the
          package keeps its days.
        </p>
      ) : choice.apply ? (
        <PackageBalance pkg={chosen} variant="checkout-preview" daysToDeduct={daysNeeded} />
      ) : (
        <p
          className="ferro-field__hint ferro-package-apply__off"
          data-testid={`package-apply-off${suffix}`}
        >
          Not applied — this visit will be charged {formatMoney(chargeCents)} and the
          package keeps its {chosen.remainingDays} day{chosen.remainingDays === 1 ? '' : 's'}.
        </p>
      )}
    </div>
  );
}
